/**
 * Feature Tour Step Definitions
 *
 * Defines all steps for the interactive feature tour that
 * guides new users through the app's main features.
 */

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface TourStepConfig {
  /** Unique identifier for the step */
  id: string;
  /** ID of the DOM element to highlight */
  targetId: string;
  /** Step title */
  title: string;
  /** Step description */
  description: string;
  /** Tooltip placement relative to target */
  placement: TourPlacement;
  /** Extra padding around the spotlight cutout */
  spotlightPadding?: number;
  /** Whether user must interact with target to proceed */
  requiresInteraction?: boolean;
  /** Type of interaction required */
  interactionType?: 'tap' | 'navigate';
  /** Lucide icon name for the step */
  icon?: string;
  /** Accent color CSS variable */
  accentColor?: string;
}

export const TOUR_STEPS: TourStepConfig[] = [
  {
    id: 'welcome',
    targetId: 'tour-welcome',
    title: 'Welcome to Resilience Forge!',
    description: "Let's take a quick tour to show you around. You can skip anytime.",
    placement: 'center',
    icon: 'Sparkles',
    accentColor: 'var(--accent-growth)',
  },
  {
    id: 'bottom-nav',
    targetId: 'tour-bottom-nav',
    title: 'Your Navigation Hub',
    description: 'These 5 tabs are your main tools for building resilience. Each one serves a different purpose.',
    placement: 'top',
    spotlightPadding: 8,
    icon: 'Navigation',
    accentColor: 'var(--text-primary)',
  },
  {
    id: 'nav-learn',
    targetId: 'tour-nav-learn',
    title: 'Learn - Brain Science',
    description: 'Discover how your brain works and why struggles actually make you stronger. Tap to explore!',
    placement: 'top',
    requiresInteraction: true,
    interactionType: 'tap',
    icon: 'Brain',
    accentColor: 'var(--accent-growth)',
  },
  {
    id: 'nav-reset',
    targetId: 'tour-nav-reset',
    title: 'Reset - AWU Protocol',
    description: 'Transform stress into focus with Acknowledge-Welcome-Utilize. Your secret weapon for pressure moments.',
    placement: 'top',
    requiresInteraction: true,
    interactionType: 'tap',
    icon: 'Zap',
    accentColor: 'var(--accent-stress)',
  },
  {
    id: 'nav-calm',
    targetId: 'tour-nav-calm',
    title: 'Calm - Coping Toolkit',
    description: 'Breathing exercises and techniques to bring your nervous system back to balance.',
    placement: 'top',
    requiresInteraction: true,
    interactionType: 'tap',
    icon: 'Wind',
    accentColor: 'var(--accent-calm)',
  },
  {
    id: 'nav-growth',
    targetId: 'tour-nav-growth',
    title: 'Growth - Your Progress',
    description: 'Track your achievements, streaks, and see how far you\'ve come on your resilience journey.',
    placement: 'top',
    requiresInteraction: true,
    interactionType: 'tap',
    icon: 'BarChart3',
    accentColor: 'var(--accent-growth)',
  },
  {
    id: 'brain-points',
    targetId: 'tour-brain-points',
    title: 'Growth Points (GP)',
    description: 'Earn GP by practicing techniques and completing activities. Use them in the shop for rewards and power-ups!',
    placement: 'bottom',
    spotlightPadding: 4,
    icon: 'Brain',
    accentColor: 'var(--accent-growth)',
  },
  {
    id: 'quick-actions',
    targetId: 'tour-quick-actions',
    title: 'Quick Actions',
    description: 'Journal your thoughts, do a quick AWU session, or get help - always just one tap away.',
    placement: 'top',
    spotlightPadding: 4,
    icon: 'Zap',
    accentColor: 'var(--accent-stress)',
  },
  {
    id: 'mood-checkin',
    targetId: 'tour-mood-checkin',
    title: 'Daily Check-In',
    description: 'Start each day by checking in with how you feel. Building self-awareness is the first step to resilience!',
    placement: 'bottom',
    spotlightPadding: 8,
    icon: 'Heart',
    accentColor: 'var(--accent-calm)',
  },
  {
    id: 'complete',
    targetId: 'tour-complete',
    title: "You're Ready!",
    description: "You've got the basics. Start with a mood check-in or explore any tab. Your resilience journey begins now!",
    placement: 'center',
    icon: 'Rocket',
    accentColor: 'var(--accent-growth)',
  },
];

/**
 * Get a tour step by ID
 */
export function getTourStep(id: string): TourStepConfig | undefined {
  return TOUR_STEPS.find(step => step.id === id);
}

/**
 * Get the index of a tour step by ID
 */
export function getTourStepIndex(id: string): number {
  return TOUR_STEPS.findIndex(step => step.id === id);
}
